import React, { useState } from "react";
import { Col, Row, Spinner, Form } from "react-bootstrap";
import { FaCircleXmark, FaCheck } from "react-icons/fa6";
import { FaDollarSign, FaShieldAlt } from "react-icons/fa";
import BeatLoader from "react-spinners/BeatLoader";
import { formatedDate } from "../../constants";
import { Description, SwipperComponent, Button } from "../CustomComponents";
import ModalContainer from "../ModalContainer";
import UserAvatarWithNameAndTag from "../UserAvatar/UserAvatarWithNameAndTag";
import cssStyles from "./styles/viewPostModal.module.scss";
import { useGetBookingByIdQuery } from "../../Data/services/bookingApi";
import {
  useResolveDisputeMutation,
  useResolveDepositEscrowMutation,
} from "../../Data/services/disputeApi";
import Toast, { SuccessToast } from "../../Hooks/useToast";

const DisputeDetailModal = ({
  handleViewModalClose,
  data,
  isLoading = false,
  refetch,
}) => {
  const [renterAmount, setRenterAmount] = useState("");
  const [ownerAmount, setOwnerAmount] = useState("");
  const [adminComments, setAdminComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch linked booking if bookingId is present
  const { data: bookingData, isLoading: isBookingLoading } =
    useGetBookingByIdQuery(data?.bookingId, {
      skip: !data?.bookingId,
    });

  const booking = bookingData?.data;

  // Mutations for resolving dispute and settling deposit escrow
  const [resolveDispute] = useResolveDisputeMutation();
  const [resolveDepositEscrow] = useResolveDepositEscrowMutation();

  const isResolved = data?.status === "resolved";

  const originalAmount = booking?.amount ?? data?.originalAmount ?? null;
  const securityDeposit =
    booking?.depositAmount ??
    booking?.listing?.depositAmount ??
    data?.securityDeposit ??
    null;
  const damageClaims = data?.refundAmount ?? null;

  // Involved users: Renter & Owner
  const renter = booking?.user || data?.user;
  const owner = booking?.requestedTo || booking?.listing?.user || null;

  const handleSubmitResolution = async (e) => {
    e.preventDefault();

    if (isResolved) return;

    setIsSubmitting(true);
    try {
      // 1. If bookingId exists, settle the deposit escrow
      if (data?.bookingId) {
        const payload = {
          bookingId: Number(data.bookingId),
          renterAmount: Number(renterAmount) || 0,
          ownerAmount: Number(ownerAmount) || 0,
          amount: (Number(renterAmount) || 0) + (Number(ownerAmount) || 0),
          disputeId: Number(data.id),
        };
        const escrowRes = await resolveDepositEscrow(payload);
        if (escrowRes?.error) {
          const errMsg =
            escrowRes.error?.data?.message ||
            escrowRes.error?.message ||
            "Failed to resolve deposit escrow";
          Toast(errMsg, "error");
          setIsSubmitting(false);
          return;
        }
      }

      // 2. Mark the dispute status as resolved
      const disputeRes = await resolveDispute(data?.id);
      if (disputeRes?.error) {
        const errMsg =
          disputeRes.error?.data?.message ||
          disputeRes.error?.message ||
          "Failed to update dispute status";
        Toast(errMsg, "error");
        setIsSubmitting(false);
        return;
      }

      SuccessToast("Dispute resolved and deposit settled successfully!");
      if (refetch) {
        await refetch();
      }
      handleViewModalClose();
    } catch (error) {
      Toast(error?.message || "An unexpected error occurred", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <ModalContainer handleClose={handleViewModalClose}>
        <div className={cssStyles.modalContainer} style={{ maxWidth: "860px" }}>
          {isLoading || isBookingLoading ? (
            <div className="h-100 d-flex justify-content-center align-items-center py-5">
              <Spinner className="secondary-text" />
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="d-flex align-items-center w-100 justify-content-between mb-3 border-bottom pb-2">
                <div className="d-flex align-items-center gap-2">
                  <p className="fs-4 fw-semibold m-0 black-text text-capitalize">
                    Dispute #{data?.id}
                  </p>
                  <span
                    className={`badge px-2 py-1 text-uppercase ${
                      isResolved
                        ? "bg-success bg-opacity-10 text-success border border-success"
                        : "bg-warning bg-opacity-10 text-warning border border-warning"
                    }`}
                  >
                    {data?.status || "pending"}
                  </span>
                  {data?.disputeType && (
                    <span className="badge bg-secondary bg-opacity-10 text-dark">
                      Type: {data?.disputeType}
                    </span>
                  )}
                </div>
                <span className="bg-white bg-opacity-75 d-flex p-2 rounded-5">
                  <FaCircleXmark
                    role="button"
                    onClick={handleViewModalClose}
                    style={{ cursor: "pointer", color: "black", fontSize: "20px" }}
                  />
                </span>
              </div>

              {/* Top Section: Media & Financial Overview */}
              <Row className="w-100 mb-3">
                {/* Media Carousel */}
                <Col md={6} sm={12} className="mb-3 mb-md-0" style={{ minWidth: 0, overflow: "hidden" }}>
                  <div className="bg-light p-2 rounded-3 border h-100 d-flex flex-column justify-content-center" style={{ minHeight: "260px" }}>
                    {data?.dispute_images && data.dispute_images.length === 1 ? (
                      <div className="d-flex justify-content-center align-items-center w-100 h-100">
                        <img
                          src={data.dispute_images[0]?.image}
                          alt="Dispute evidence"
                          className="rounded border"
                          loading="lazy"
                          style={{
                            maxHeight: "260px",
                            maxWidth: "100%",
                            objectFit: "contain",
                            backgroundColor: "#f8f9fa",
                          }}
                        />
                      </div>
                    ) : data?.dispute_images && data.dispute_images.length > 1 ? (
                      <div style={{ width: "100%", overflow: "hidden", minWidth: 0 }}>
                        <SwipperComponent spaceBetween={10} slidesPerView={1}>
                          {data.dispute_images.map((x) => (
                            <img
                              key={x?.id || x?.image}
                              className="rounded border w-100"
                              src={x?.image}
                              alt="Dispute evidence"
                              loading="lazy"
                              style={{
                                maxHeight: "260px",
                                objectFit: "contain",
                                backgroundColor: "#f8f9fa",
                              }}
                            />
                          ))}
                        </SwipperComponent>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <p className="m-0">No dispute images uploaded</p>
                      </div>
                    )}
                  </div>
                </Col>

                {/* Financial Summary Cards */}
                <Col md={6} sm={12}>
                  <div className="d-flex flex-column gap-2 h-100 justify-content-between">
                    <div className="p-3 bg-white border rounded-3 shadow-sm">
                      <p className="text-muted small mb-1">Original Amount</p>
                      <h4 className="fw-bold mb-0 text-dark d-flex align-items-center">
                        <FaDollarSign size={18} className="text-success me-1" />
                        {originalAmount !== null ? originalAmount : "-"}
                      </h4>
                      <small className="text-muted">Rental booking fee</small>
                    </div>

                    <div className="p-3 bg-white border rounded-3 shadow-sm">
                      <p className="text-muted small mb-1">Damage Claims</p>
                      <h4 className="fw-bold mb-0 text-danger d-flex align-items-center">
                        <FaDollarSign size={18} className="text-danger me-1" />
                        {damageClaims !== null ? damageClaims : "-"}
                      </h4>
                      <small className="text-muted">Amount claimed for damages</small>
                    </div>

                    <div className="p-3 bg-white border rounded-3 shadow-sm">
                      <p className="text-muted small mb-1">Security Deposit</p>
                      <h4 className="fw-bold mb-0 text-primary d-flex align-items-center">
                        <FaShieldAlt size={18} className="text-primary me-1" />
                        {securityDeposit !== null ? securityDeposit : "-"}
                      </h4>
                      <small className="text-muted">
                        Deposit specified for equipment
                      </small>
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Involved Users */}
              <Row className="w-100 mb-3">
                <Col md={6} sm={12} className="mb-2 mb-md-0">
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100">
                    <span className="badge bg-primary bg-opacity-10 text-primary mb-2">
                      Renter (Client)
                    </span>
                    {renter ? (
                      <div>
                        <UserAvatarWithNameAndTag
                          avatarImage={
                            renter?.profileImage ||
                            renter?.user_images?.[0]?.image_url ||
                            "/images/user-placeholder.jpeg"
                          }
                          userName={renter?.displayName || renter?.firstName || "Renter"}
                          userTag={renter?.email || ""}
                        />
                      </div>
                    ) : (
                      <p className="text-muted mb-0">Renter details unavailable</p>
                    )}
                  </div>
                </Col>
                <Col md={6} sm={12}>
                  <div className="p-3 border rounded-3 bg-white shadow-sm h-100">
                    <span className="badge bg-info bg-opacity-10 text-info mb-2">
                      Equipment Owner (Host)
                    </span>
                    {owner ? (
                      <div>
                        <UserAvatarWithNameAndTag
                          avatarImage={
                            owner?.profileImage ||
                            owner?.user_images?.[0]?.image_url ||
                            "/images/user-placeholder.jpeg"
                          }
                          userName={owner?.displayName || owner?.firstName || "Owner"}
                          userTag={owner?.email || ""}
                        />
                      </div>
                    ) : (
                      <p className="text-muted mb-0">Owner details unavailable</p>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Comments / Complaints */}
              <div className="w-100 p-3 bg-white border rounded-3 shadow-sm mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <p className="fw-bold mb-0 text-dark">Comments / Complaints</p>
                  <small className="text-muted">
                    Created: {formatedDate(data?.createdAt)}
                  </small>
                </div>
                {data?.reason && (
                  <div className="mb-2">
                    <span className="badge bg-warning bg-opacity-25 text-dark">
                      Reason: {data?.reason}
                    </span>
                  </div>
                )}
                <div className="p-2 bg-light rounded text-dark border">
                  {data?.description || "No additional comments provided."}
                </div>
              </div>

              {/* Resolution & Allocation Form */}
              <div className="w-100 p-3 bg-white border rounded-3 shadow-sm">
                <p className="fw-bold text-dark mb-2">
                  Amount Allocation & Resolution
                </p>

                {isResolved ? (
                  <div className="alert alert-success d-flex align-items-center mb-0">
                    <FaCheck className="me-2" />
                    <span>
                      This dispute has already been resolved and closed.
                    </span>
                  </div>
                ) : (
                  <Form onSubmit={handleSubmitResolution}>
                    <Row className="mb-3">
                      <Col md={6} sm={12} className="mb-2 mb-md-0">
                        <Form.Group controlId="renterAllocation">
                          <Form.Label className="small text-muted mb-1">
                            Amount Allocated to Renter ($)
                          </Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={renterAmount}
                            onChange={(e) => setRenterAmount(e.target.value)}
                            className="p-2"
                          />
                          <Form.Text className="text-muted">
                            Refund awarded to the renter
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col md={6} sm={12}>
                        <Form.Group controlId="ownerAllocation">
                          <Form.Label className="small text-muted mb-1">
                            Amount Allocated to Owner ($)
                          </Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={ownerAmount}
                            onChange={(e) => setOwnerAmount(e.target.value)}
                            className="p-2"
                          />
                          <Form.Text className="text-muted">
                            Amount released to equipment owner
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group controlId="adminComments">
                          <Form.Label className="small text-muted mb-1">
                            Admin Comments
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            placeholder="Add administrative remarks or resolution notes..."
                            value={adminComments}
                            onChange={(e) => setAdminComments(e.target.value)}
                            className="p-2"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        classes="btn btn-secondary px-3 py-2 text-white"
                        text="Cancel"
                        onClick={handleViewModalClose}
                        disabled={isSubmitting}
                      />
                      <Button
                        type="submit"
                        classes="btn btn-primary px-4 py-2 text-white"
                        text={
                          isSubmitting ? (
                            <BeatLoader size={8} color="#fff" />
                          ) : (
                            "Resolve Dispute"
                          )
                        }
                        disabled={isSubmitting}
                      />
                    </div>
                  </Form>
                )}
              </div>
            </>
          )}
        </div>
      </ModalContainer>
    </div>
  );
};

export default DisputeDetailModal;
